import { candles } from '~data';
import { transactions } from '~/stores/transactions-store';
import { costOf, proceedsOf } from '~/stores/positions';

/**
 * What an account has been worth, day by day.
 *
 * Rebuilt from the blotter rather than stored: units held on a date are the
 * trades up to that date, and a value is those units against that day's close.
 * Nothing is recorded twice, so nothing can disagree.
 *
 * Two lines come out of it. Value is what the account is worth; cost is what
 * went into it, which is the only thing that makes the first one readable — an
 * account that doubled because you put more in looks exactly like one that
 * doubled because it grew, until the two are drawn together.
 *
 * A sale moves units into cash rather than out of the account, and both lines
 * are built to say so. The proceeds stay in `value`, so selling out at a profit
 * leaves the line where the sale put it instead of dropping it to zero; and they
 * are spent before new money is on the next buy, so recycling one position into
 * another does not read as a fresh contribution. `cost` is money from outside
 * only, which is why it still steps upward and never down: what you have put in
 * is not undone by taking something back out.
 */
export interface AccountPoint {
    t: number;
    value: number;
    /** Cumulative money from outside the account, commissions included. */
    cost: number;
}

export const accountSeries = (accountId: string): AccountPoint[] => {
    // Oldest first, ties broken the way the blotter breaks them, because a sale
    // relieves the average cost standing at the moment it happened.
    const trades = transactions
        .filter((t) => t.accountId === accountId)
        .slice()
        .sort((a, b) => a.date.localeCompare(b.date) || (a.createdAt ?? '').localeCompare(b.createdAt ?? '') || a.id.localeCompare(b.id));
    if (trades.length === 0) return [];

    const symbols = [...new Set(trades.map((t) => t.symbol))];

    // One pass per symbol with a cursor, rather than a lookup per symbol per
    // day. A five-year account across six funds is 7,500 date lookups, and the
    // naive version rescans the whole price series for each one.
    const prices = new Map(symbols.map((s) => [s, candles(s)]));
    const cursor = new Map(symbols.map((s) => [s, 0]));
    const held = new Map<string, number>();
    const lastClose = new Map<string, number>();

    // Every trading day any held fund has a close for, from the first trade on.
    const first = trades[0].date;
    const dates = [...new Set(symbols.flatMap((s) => (prices.get(s) ?? []).map((c) => c.date)))].filter((d) => d >= first).sort();

    const out: AccountPoint[] = [];
    let next = 0;
    let cost = 0;
    /** Sale proceeds not yet put back to work. */
    let cash = 0;

    for (const date of dates) {
        // Everything traded on or before this day. A trade dated on a holiday
        // still counts from that day — it is when the money moved.
        while (next < trades.length && trades[next].date <= date) {
            const t = trades[next];
            if (t.kind === 'sell') {
                held.set(t.symbol, Math.max(0, (held.get(t.symbol) ?? 0) - t.units));
                cash += proceedsOf(t);
            } else {
                held.set(t.symbol, (held.get(t.symbol) ?? 0) + t.units);
                // A loan-funded buy was paid by the lender and draws nothing
                // down; a cash one spends the proceeds it is sitting on before
                // it counts as anything new going in.
                const spent = t.loanId == null ? Math.min(cash, costOf(t)) : 0;
                cash -= spent;
                cost += costOf(t) - spent;
            }
            next += 1;
        }

        let value = cash;
        for (const [symbol, units] of held) {
            const series = prices.get(symbol) ?? [];
            let at = cursor.get(symbol) ?? 0;
            while (at < series.length && series[at].date <= date) {
                lastClose.set(symbol, series[at].close);
                at += 1;
            }
            cursor.set(symbol, at);
            // The last close on or before today, so a fund that did not trade
            // carries rather than dropping out of the total and putting a notch
            // in the line.
            const close = lastClose.get(symbol);
            if (close != null) value += units * close;
        }

        out.push({ t: Date.parse(date), value, cost });
    }

    return out;
};
